import { useState, type FormEvent } from 'react';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Select from '@/components/base/Select';
import Textarea from '@/components/base/Textarea';
import FileUpload from '@/components/base/FileUpload';
import SectionCard from '@/components/base/SectionCard';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { useToast } from '@/components/feature/ToastProvider';
import { saveProject } from '@/lib/projectService';
import type { Project, ProjectStatus } from '@/types';

const STATUS_OPTIONS = [
  { value: 'EN PROCESO', label: 'En proceso' },
  { value: 'ACTIVO', label: 'Activo' },
  { value: 'DESACTIVADO', label: 'Desactivado' },
];

const BILLING_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => ({
  value: String(index + 1),
  label: `Día ${index + 1} de cada mes`,
}));

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_PDF_BYTES = 10 * 1024 * 1024;

interface ProjectFormProps {
  mode: 'create' | 'edit';
  initial?: Project | null;
  onSaved: (project: Project) => void;
  onCancel: () => void;
}

function fileNameFromPath(path: string | null | undefined): string | null {
  if (!path) return null;
  const last = path.split('/').pop() ?? path;
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

/** Valida que el texto sea una URL http(s) con dominio, sin ser restrictivo de más. */
function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.');
  } catch {
    return false;
  }
}

export default function ProjectForm({ mode, initial, onSaved, onCancel }: ProjectFormProps) {
  const { notify } = useToast();
  const isEdit = mode === 'edit';

  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [status, setStatus] = useState<ProjectStatus>(initial?.status ?? 'EN PROCESO');
  const [billingDay, setBillingDay] = useState(initial?.billing_day ? String(initial.billing_day) : '');
  const [monthlyAmount, setMonthlyAmount] = useState(
    initial?.monthly_amount !== null && initial?.monthly_amount !== undefined
      ? String(initial.monthly_amount)
      : '',
  );
  const [developmentStart, setDevelopmentStart] = useState(initial?.development_start ?? '');
  const [developmentEnd, setDevelopmentEnd] = useState(initial?.development_end ?? '');
  const [contractEnd, setContractEnd] = useState(initial?.contract_end ?? '');
  const [linkProyecto, setLinkProyecto] = useState(initial?.link_proyecto ?? '');
  const [clientName, setClientName] = useState(initial?.clients?.name ?? '');
  const [clientPhone, setClientPhone] = useState(initial?.clients?.contact_phone ?? '');

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeContract, setRemoveContract] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const existingLogoUrl = removeLogo ? null : initial?.logo_url ?? null;
  const existingContractUrl = removeContract ? null : initial?.contract_document_url ?? null;

  const handleLogoChange = (file: File | null) => {
    if (!file) {
      setLogoFile(null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, logo: 'El archivo debe ser una imagen (JPG, PNG o WEBP).' }));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setErrors((prev) => ({ ...prev, logo: 'La imagen no debe superar los 5 MB.' }));
      return;
    }
    clearError('logo');
    setRemoveLogo(false);
    setLogoFile(file);
  };

  const handleContractChange = (file: File | null) => {
    if (!file) {
      setContractFile(null);
      return;
    }
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setErrors((prev) => ({ ...prev, contract: 'El documento debe estar en formato PDF.' }));
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      setErrors((prev) => ({ ...prev, contract: 'El documento no debe superar los 10 MB.' }));
      return;
    }
    clearError('contract');
    setRemoveContract(false);
    setContractFile(file);
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'El nombre del proyecto es obligatorio.';
    if (!developmentStart) next.developmentStart = 'La fecha de inicio del desarrollo es obligatoria.';
    if (!developmentEnd) next.developmentEnd = 'La fecha de fin del desarrollo es obligatoria.';
    if (developmentStart && developmentEnd && developmentEnd < developmentStart) {
      next.developmentEnd = 'La fecha de fin no puede ser anterior a la fecha de inicio.';
    }
    if (!billingDay) next.billingDay = 'Seleccione el día de cobro mensual.';
    const amountValue = Number(monthlyAmount);
    if (!monthlyAmount.trim()) next.monthlyAmount = 'El monto de mensualidad es obligatorio.';
    else if (Number.isNaN(amountValue) || amountValue <= 0) {
      next.monthlyAmount = 'Ingrese un monto válido mayor a cero.';
    }
    if (!contractEnd) next.contractEnd = 'La fecha de fin de contrato es obligatoria.';
    const trimmedLink = linkProyecto.trim();
    if (trimmedLink && !isValidUrl(trimmedLink)) {
      next.linkProyecto = 'Ingresa una URL válida.';
    }
    if (!clientName.trim()) next.clientName = 'El nombre del cliente es obligatorio.';
    const digits = clientPhone.replace(/\D/g, '');
    if (!clientPhone.trim()) next.clientPhone = 'El número de celular es obligatorio.';
    else if (digits.length < 7) next.clientPhone = 'Ingrese un número de celular válido.';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const performSave = async () => {
    setSaving(true);
    try {
      const project = await saveProject({
        id: initial?.id,
        name,
        description,
        status,
        billing_day: billingDay ? Number(billingDay) : null,
        monthly_amount: monthlyAmount.trim() ? Number(monthlyAmount) : null,
        development_start: developmentStart || null,
        development_end: developmentEnd || null,
        contract_start: initial?.contract_start ?? (developmentStart || null),
        contract_end: contractEnd || null,
        link_proyecto: linkProyecto.trim() || null,
        clientName,
        clientPhone,
        logoFile,
        contractFile,
        currentLogoUrl: initial?.logo_url ?? null,
        currentContractUrl: initial?.contract_document_url ?? null,
        removeLogo,
        removeContract,
      });
      notify({
        tone: 'success',
        title: isEdit ? 'Cambios guardados correctamente.' : 'Proyecto creado correctamente.',
        message: `${project.name} fue ${isEdit ? 'actualizado' : 'registrado'} con éxito.`,
      });
      onSaved(project);
    } catch {
      notify({
        tone: 'error',
        title: 'No se pudo guardar el proyecto',
        message: 'Ocurrió un problema al guardar la información. Intente nuevamente.',
      });
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) {
      notify({
        tone: 'error',
        title: 'Revise la información',
        message: 'Complete los campos obligatorios marcados en rojo.',
      });
      return;
    }
    if (isEdit) {
      setConfirmOpen(true);
      return;
    }
    performSave();
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
        <SectionCard title="Información general" icon="ri-information-line">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <FileUpload
                label="Imagen del logo"
                accept="image/png,image/jpeg,image/webp,image/gif"
                kind="image"
                file={logoFile}
                existingUrl={existingLogoUrl}
                existingLabel={fileNameFromPath(initial?.logo_url)}
                onFileChange={handleLogoChange}
                onRemoveExisting={() => {
                  setLogoFile(null);
                  setRemoveLogo(true);
                  clearError('logo');
                }}
                error={errors.logo}
                hint="JPG, PNG o WEBP · máx. 5 MB"
              />
            </div>
            <div className="flex flex-col gap-4 lg:col-span-2">
              <Input
                label="* Nombre del proyecto"
                name="name"
                icon="ri-folders-line"
                placeholder="Ej: Sistema de Gestión Farmacéutica"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  clearError('name');
                }}
                error={errors.name}
              />
              <Select
                label="Estado del proyecto"
                name="status"
                value={status}
                onChange={(event) => setStatus(event.target.value as ProjectStatus)}
                options={STATUS_OPTIONS}
              />
            </div>
          </div>
          <div className="mt-4">
            <Textarea
              label="Descripción"
              name="description"
              rows={3}
              maxLength={500}
              showCounter
              placeholder="Describa brevemente el alcance del proyecto..."
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          <div className="mt-4">
            <Input
              label="Link del proyecto"
              name="linkProyecto"
              type="url"
              inputMode="url"
              icon="ri-links-line"
              placeholder="https://ejemplo.com"
              value={linkProyecto}
              onChange={(event) => {
                setLinkProyecto(event.target.value);
                clearError('linkProyecto');
              }}
              error={errors.linkProyecto}
              hint="Opcional. Enlace público del proyecto."
            />
          </div>
        </SectionCard>

        <SectionCard title="Desarrollo y contrato" icon="ri-calendar-check-line">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="* Fecha de inicio del desarrollo"
              name="developmentStart"
              type="date"
              value={developmentStart}
              onChange={(event) => {
                setDevelopmentStart(event.target.value);
                clearError('developmentStart');
                clearError('developmentEnd');
              }}
              error={errors.developmentStart}
            />
            <Input
              label="* Fecha fin del desarrollo"
              name="developmentEnd"
              type="date"
              value={developmentEnd}
              min={developmentStart || undefined}
              onChange={(event) => {
                setDevelopmentEnd(event.target.value);
                clearError('developmentEnd');
              }}
              error={errors.developmentEnd}
            />
            <Select
              label="* Fecha de cobro de cada mes"
              name="billingDay"
              placeholder="Seleccione el día de cobro"
              value={billingDay}
              onChange={(event) => {
                setBillingDay(event.target.value);
                clearError('billingDay');
              }}
              options={BILLING_DAY_OPTIONS}
              error={errors.billingDay}
              hint="Se usa para calcular automáticamente el próximo cobro."
            />
            <Input
              label="* Monto de mensualidad (Bs.)"
              name="monthlyAmount"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              icon="ri-money-dollar-circle-line"
              placeholder="Ej: 1500.00"
              value={monthlyAmount}
              onChange={(event) => {
                setMonthlyAmount(event.target.value);
                clearError('monthlyAmount');
              }}
              error={errors.monthlyAmount}
              hint="Monto que el cliente paga mensualmente por el proyecto."
            />
            <Input
              label="* Fecha fin de contrato"
              name="contractEnd"
              type="date"
              value={contractEnd}
              onChange={(event) => {
                setContractEnd(event.target.value);
                clearError('contractEnd');
              }}
              error={errors.contractEnd}
              hint="Se usa para el recordatorio de finalización de contrato."
            />
          </div>
          <div className="mt-4">
            <FileUpload
              label="Documento de contrato (PDF)"
              accept="application/pdf"
              kind="file"
              file={contractFile}
              existingUrl={existingContractUrl}
              existingLabel={fileNameFromPath(initial?.contract_document_url)}
              onFileChange={handleContractChange}
              onRemoveExisting={() => {
                setContractFile(null);
                setRemoveContract(true);
                clearError('contract');
              }}
              error={errors.contract}
              hint="Solo formato PDF · máx. 10 MB"
            />
          </div>
        </SectionCard>

        <SectionCard title="Información del cliente" icon="ri-user-3-line">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="* Nombre del cliente"
              name="clientName"
              icon="ri-building-2-line"
              placeholder="Ej: Farmacia Central"
              value={clientName}
              onChange={(event) => {
                setClientName(event.target.value);
                clearError('clientName');
              }}
              error={errors.clientName}
            />
            <Input
              label="* Número de celular"
              name="clientPhone"
              icon="ri-smartphone-line"
              placeholder="Ej: +591 71234567"
              value={clientPhone}
              onChange={(event) => {
                setClientPhone(event.target.value);
                clearError('clientPhone');
              }}
              error={errors.clientPhone}
              hint="Número de contacto del cliente."
            />
          </div>
        </SectionCard>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" icon="ri-arrow-left-line" onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" icon="ri-save-line" loading={saving}>
            {isEdit ? 'Guardar cambios' : 'Guardar proyecto'}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={confirmOpen}
        title="¿Está seguro de que desea guardar los cambios realizados en este proyecto?"
        description="Se actualizará la información del proyecto en el sistema."
        confirmLabel="Guardar cambios"
        cancelLabel="Cancelar"
        icon="ri-save-3-line"
        loading={saving}
        onConfirm={performSave}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}